import { useEffect, useState, useRef } from 'react'
import { Search, Plus, X, Archive, RotateCcw, User, Pencil, ExternalLink, Upload, Download, CheckCircle, AlertCircle, FileDown, Filter } from 'lucide-react'
import { Link } from 'react-router-dom'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'
import { useModules } from '../lib/modules'
import { useExcelOrder, sortByExcelOrder } from '../lib/excelOrder'

interface Employee {
  id: string; firstName: string; lastName: string; email: string | null
  phone: string | null; contractType: string; weeklyHours: number
  status: string; establishmentId: string; establishment: { name: string }
  hourlyRate: number; conventionLevel: string; matricule: string | null
  position: { id: string; name: string; color: string } | null
}
interface Establishment { id: string; name: string }

const CONTRACT_COLORS: Record<string, string> = {
  CDI: 'bg-green-100 text-green-800',
  CDD: 'bg-blue-100 text-blue-800',
  Saisonnier: 'bg-orange-100 text-orange-800',
  Extra: 'bg-purple-100 text-purple-800',
  Stage: 'bg-amber-100 text-amber-800',
  Intérim: 'bg-gray-100 text-gray-800',
}

// Grille IDCC 1505
const CCN_LEVELS = [
  { value: 'E1', label: 'E1 — Employé niv. 1', rate: 12.063 },
  { value: 'E2', label: 'E2 — Employé niv. 2', rate: 12.134 },
  { value: 'E3', label: 'E3 — Employé niv. 3', rate: 12.147 },
  { value: 'E4', label: 'E4 — Employé niv. 4', rate: 12.400 },
  { value: 'E5', label: 'E5 — Employé niv. 5', rate: 12.486 },
  { value: 'E6', label: 'E6 — Employé niv. 6', rate: 12.785 },
  { value: 'E7', label: 'E7 — Employé niv. 7', rate: 12.882 },
  { value: 'AM1', label: 'AM1 — Agent de maîtrise 1', rate: 16.023 },
  { value: 'AM2', label: 'AM2 — Agent de maîtrise 2', rate: 16.296 },
  { value: 'C1', label: 'C1 — Cadre 1', rate: 19.929 },
  { value: 'C2', label: 'C2 — Cadre 2', rate: 22.077 },
]

export default function Employees() {
  const [employees, setEmployees] = useState<Employee[]>([])
  const { establishments, current: currentEstab } = useEstablishment()
  const { modules } = useModules()
  const excelOrder = useExcelOrder(currentEstab?.id)
  const isAdmin = ['super_admin', 'admin'].includes(JSON.parse(localStorage.getItem('user') || '{}').role)

  // Même code de connexion à l'appli pour tout le personnel actif (par défaut 1234)
  async function resetAllPins() {
    const pin = prompt('Code de connexion à donner à TOUT le personnel actif (4 chiffres) :', '1234')
    if (pin === null) return
    if (!/^\d{4}$/.test(pin)) { alert('Le code doit faire 4 chiffres.'); return }
    if (!confirm(`Tout le personnel actif pourra se connecter à l'appli avec le code ${pin}. Continuer ?`)) return
    try {
      const r = await api.post('/employees/reset-pins', { pin })
      alert(`${r.data.updated} employé(s) : code ${pin} enregistré.`)
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Enregistrement refusé')
    }
  }
  const [search, setSearch] = useState('')
  const [filterContract, setFilterContract] = useState('')
  const [filterPosition, setFilterPosition] = useState('')
  const [positions, setPositions] = useState<{id:string;name:string;color:string}[]>([])
  const [showArchived, setShowArchived] = useState(false)
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<'create' | 'edit' | 'import' | null>(null)
  const [importRows, setImportRows] = useState<any[]>([])
  const [importResult, setImportResult] = useState<{ created: number; errors: any[] } | null>(null)
  const [importing, setImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    contractType: 'CDI', weeklyHours: 35, establishmentId: '',
    conventionLevel: 'E1', hourlyRate: 12.063
  })

  async function load() {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.set('status', showArchived ? 'archived' : 'active')
      if (search) params.set('search', search)
      if (currentEstab) params.set('establishmentId', currentEstab.id)
      if (filterContract) params.set('contractType', filterContract)
      if (filterPosition) params.set('positionId', filterPosition)
      const res = await api.get(`/employees?${params}`)
      setEmployees(res.data)
      if (!form.establishmentId && currentEstab) {
        setForm(f => ({ ...f, establishmentId: currentEstab.id }))
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    api.get('/positions').then(r => setPositions(r.data)).catch(() => {})
  }, [])

  useEffect(() => { load() }, [showArchived, search, currentEstab, filterContract, filterPosition])

  async function exportCSV() {
    const params = new URLSearchParams()
    params.set('status', showArchived ? 'archived' : 'active')
    if (currentEstab) params.set('establishmentId', currentEstab.id)
    const link = document.createElement('a')
    link.href = `/api/employees/export?${params}`
    link.download = 'employes.csv'
    link.click()
  }

  function handleLevelChange(level: string) {
    const found = CCN_LEVELS.find(l => l.value === level)
    setForm(f => ({ ...f, conventionLevel: level, hourlyRate: found?.rate ?? f.hourlyRate }))
  }

  function openEdit(emp: Employee & { establishmentId?: string }) {
    setForm({
      firstName: emp.firstName,
      lastName: emp.lastName,
      email: emp.email || '',
      phone: emp.phone || '',
      contractType: emp.contractType,
      weeklyHours: emp.weeklyHours,
      establishmentId: emp.establishmentId || currentEstab?.id || '',
      conventionLevel: emp.conventionLevel || 'E1',
      hourlyRate: emp.hourlyRate || 12.063
    })
    setEditingId(emp.id)
    setModal('edit')
  }

  async function saveEdit() {
    if (!editingId) return
    setSaving(true)
    setSaveError('')
    try {
      const res = await api.put(`/employees/${editingId}`, form)
      setEmployees(prev => prev.map(e => e.id === editingId ? res.data : e))
      setModal(null)
      setEditingId(null)
    } catch (e: any) {
      setSaveError(e.response?.data?.error || 'Erreur lors de l\'enregistrement. Vérifiez que la base de données est à jour (prisma db push).')
    } finally {
      setSaving(false)
    }
  }

  async function createEmployee() {
    if (!form.firstName || !form.lastName) return
    setSaving(true)
    try {
      const res = await api.post('/employees', form)
      setEmployees(prev => [res.data, ...prev])
      setModal(null)
      setForm(f => ({ ...f, firstName: '', lastName: '', email: '', phone: '' }))
    } finally {
      setSaving(false)
    }
  }

  async function archiveEmployee(id: string) {
    if (!confirm('Archiver cet employé ?')) return
    await api.post(`/employees/${id}/archive`)
    setEmployees(prev => prev.filter(e => e.id !== id))
  }

  function downloadTemplate() {
    const csv = 'firstName;lastName;email;phone;contractType;weeklyHours;conventionLevel;hourlyRate\nThomas;Martin;thomas.martin@exemple.fr;06 00 00 00 00;CDI;35;E1;12.063'
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'modele_import_employes.csv'
    a.click()
  }

  function parseCSV(text: string) {
    const lines = text.trim().split(/\r?\n/)
    if (lines.length < 2) return []
    const sep = lines[0].includes(';') ? ';' : ','
    const headers = lines[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ''))
    return lines.slice(1).map(line => {
      const cols = line.split(sep).map(c => c.trim().replace(/^"|"$/g, ''))
      const obj: Record<string, string> = {}
      headers.forEach((h, i) => { obj[h] = cols[i] || '' })
      return obj
    }).filter(r => r.firstName || r.lastName)
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const text = ev.target?.result as string
      const rows = parseCSV(text)
      setImportRows(rows)
      setImportResult(null)
    }
    reader.readAsText(file, 'UTF-8')
  }

  async function runImport() {
    if (!importRows.length || !currentEstab) return
    setImporting(true)
    try {
      const res = await api.post('/employees/import', {
        employees: importRows,
        establishmentId: currentEstab.id
      })
      setImportResult(res.data)
      if (res.data.created > 0) load()
    } finally {
      setImporting(false)
    }
  }

  async function restoreEmployee(id: string) {
    await api.post(`/employees/${id}/restore`)
    setEmployees(prev => prev.filter(e => e.id !== id))
  }

  const label = 'block text-sm font-medium text-gray-700 mb-1'
  const input = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="p-3 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 md:mb-6 gap-2 flex-wrap">
        <h1 className="text-xl font-bold text-gray-900">Employés</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <button onClick={resetAllPins} title="Même code de connexion à l'appli pour tout le personnel"
              className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
              🔑 <span>Code 1234 pour tous</span>
            </button>
          )}
          <button onClick={exportCSV}
            className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
            <FileDown size={15} /> <span className="hidden sm:inline">Export CSV</span>
          </button>
          <button onClick={() => { setImportRows([]); setImportResult(null); setModal('import') }}
            className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
            <Upload size={15} /> <span className="hidden sm:inline">Importer CSV</span>
          </button>
          <button onClick={() => {
              setForm(f => ({ ...f, firstName: '', lastName: '', email: '', phone: '', establishmentId: currentEstab?.id || '' }))
              setModal('create')
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Plus size={16} /> Nouvel employé
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-48 sm:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Rechercher nom, email, matricule…" value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={filterContract} onChange={e => setFilterContract(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Tous les contrats</option>
          {['CDI','CDD','Saisonnier','Extra','Stage','Intérim'].map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterPosition} onChange={e => setFilterPosition(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Tous les postes</option>
          {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button onClick={() => setShowArchived(!showArchived)}
          className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border transition-colors ${showArchived ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
          <Archive size={15} />{showArchived ? 'Voir actifs' : 'Voir archivés'}
        </button>
        {(filterContract || filterPosition) && (
          <button onClick={() => { setFilterContract(''); setFilterPosition('') }}
            className="text-xs text-gray-500 hover:text-gray-700 underline">
            Effacer filtres
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Employé</th>
              <th className="hidden md:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact</th>
              <th className="hidden md:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Contrat</th>
              <th className="hidden sm:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Poste</th>
              {modules.rh && <th className="hidden lg:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Niveau CCN</th>}
              <th className="hidden lg:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Établissement</th>
              <th className="px-2 md:px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">Chargement...</td></tr>
            ) : employees.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">Aucun employé</td></tr>
            ) : (
              sortByExcelOrder(employees, excelOrder).map(emp => (
                <tr key={emp.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                  <td className="px-3 md:px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-gray-900 text-xs font-bold flex-shrink-0 border border-black/10"
                        style={{ backgroundColor: (emp as any).rowColor || '#C7D2FE' }}>
                        {emp.firstName[0]}{emp.lastName?.[0] || ''}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{emp.firstName} {emp.lastName}</p>
                        <p className="text-xs text-gray-400">{emp.weeklyHours}h/sem{emp.matricule ? ` · ${emp.matricule}` : ''}</p>
                        {emp.position && <p className="sm:hidden text-xs text-gray-500">{emp.position.name}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="hidden md:table-cell px-4 py-3">
                    {emp.email && <p className="text-sm text-gray-600">{emp.email}</p>}
                    {emp.phone && <p className="text-xs text-gray-400">{emp.phone}</p>}
                    {!emp.email && !emp.phone && <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="hidden md:table-cell px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit ${CONTRACT_COLORS[emp.contractType] || 'bg-gray-100 text-gray-700'}`}>
                        {emp.contractType}
                      </span>
                      <span className="text-xs text-gray-400">{emp.weeklyHours}h/sem</span>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-4 py-3">
                    {emp.position
                      ? <span className="text-xs px-2 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: emp.position.color }}>{emp.position.name}</span>
                      : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  {modules.rh && (
                  <td className="hidden lg:table-cell px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {emp.conventionLevel || 'E1'}
                      </span>
                      <span className="text-xs text-gray-400 font-mono">{emp.hourlyRate?.toFixed(3)} €/h</span>
                    </div>
                  </td>
                  )}
                  <td className="hidden lg:table-cell px-4 py-3">
                    <span className="text-sm text-gray-600">{emp.establishment?.name}</span>
                  </td>
                  <td className="px-2 md:px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2 md:gap-3 flex-wrap">
                      <Link to={`/employees/${emp.id}`}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors">
                        <ExternalLink size={14} /> Fiche
                      </Link>
                      {!showArchived && (
                        <button onClick={() => openEdit(emp)}
                          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                          <Pencil size={14} /> Modifier
                        </button>
                      )}
                      {showArchived ? (
                        <button onClick={() => restoreEmployee(emp.id)}
                          className="flex items-center gap-1.5 text-sm text-green-600 hover:text-green-800 font-medium transition-colors">
                          <RotateCcw size={14} /> Restaurer
                        </button>
                      ) : (
                        <button onClick={() => archiveEmployee(emp.id)}
                          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-red-600 transition-colors">
                          <Archive size={14} /> Archiver
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 mt-3 text-right">{employees.length} employé{employees.length > 1 ? 's' : ''}</p>

      {/* Edit Modal */}
      {modal === 'edit' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <Pencil size={18} className="text-blue-600" />
                <h2 className="font-bold text-gray-900">Modifier l'employé</h2>
              </div>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Prénom *</label>
                  <input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className={input} />
                </div>
                <div>
                  <label className={label}>Nom *</label>
                  <input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className={input} />
                </div>
              </div>
              <div>
                <label className={label}>Email</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={input} />
              </div>
              <div>
                <label className={label}>Téléphone</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={input} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Contrat</label>
                  <select value={form.contractType} onChange={e => setForm(f => ({ ...f, contractType: e.target.value }))} className={input}>
                    {['CDI', 'CDD', 'Saisonnier', 'Extra', 'Stage', 'Intérim'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Heures/semaine</label>
                  <input type="number" value={form.weeklyHours} onChange={e => setForm(f => ({ ...f, weeklyHours: Number(e.target.value) }))} min={1} max={60} className={input} />
                </div>
              </div>
              <div className="border border-blue-100 bg-blue-50 rounded-lg p-3 space-y-3">
                <p className="text-xs font-semibold text-blue-700">Convention IDCC 1505</p>
                <div>
                  <label className={label}>Niveau CCN</label>
                  <select value={form.conventionLevel} onChange={e => handleLevelChange(e.target.value)} className={input}>
                    {CCN_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label} — {l.rate.toFixed(3)} €/h</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Taux horaire brut (€/h)</label>
                  <input type="number" step="0.001" value={form.hourlyRate}
                    onChange={e => setForm(f => ({ ...f, hourlyRate: Number(e.target.value) }))}
                    className={input} />
                </div>
              </div>
              {establishments.length > 1 && (
                <div>
                  <label className={label}>Établissement</label>
                  <select value={form.establishmentId} onChange={e => setForm(f => ({ ...f, establishmentId: e.target.value }))} className={input}>
                    {establishments.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Modifier pour transférer l'employé dans un autre magasin.</p>
                </div>
              )}
            </div>
            {saveError && (
              <div className="mx-6 mb-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {saveError}
              </div>
            )}
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => { setModal(null); setSaveError('') }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                Annuler
              </button>
              <button onClick={saveEdit} disabled={saving || !form.firstName || !form.lastName}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {modal === 'import' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <Upload size={18} className="text-blue-600" />
                <h2 className="font-bold text-gray-900">Import CSV — Employés</h2>
              </div>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-3">
                <Download size={15} className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-blue-800 font-medium">Format attendu : CSV séparé par point-virgule</p>
                  <p className="text-xs text-blue-600 mt-0.5">Colonnes : firstName ; lastName ; email ; phone ; contractType ; weeklyHours ; conventionLevel ; hourlyRate</p>
                  <button onClick={downloadTemplate} className="text-xs text-blue-700 underline mt-1 hover:text-blue-900">
                    Télécharger le modèle CSV
                  </button>
                </div>
              </div>

              {/* File input */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-300 rounded-xl py-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <Upload size={24} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 font-medium">Cliquez pour sélectionner un fichier CSV</p>
                  <p className="text-xs text-gray-400 mt-1">.csv ou .txt — séparateur ; ou ,</p>
                </button>
              </div>

              {/* Preview */}
              {importRows.length > 0 && !importResult && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">{importRows.length} ligne{importRows.length > 1 ? 's' : ''} détectée{importRows.length > 1 ? 's' : ''} :</p>
                  <div className="overflow-auto max-h-40 border border-gray-200 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {['Prénom', 'Nom', 'Email', 'Contrat', 'H/sem', 'Niveau'].map(h => (
                            <th key={h} className="px-3 py-2 text-left font-semibold text-gray-500">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {importRows.slice(0, 10).map((r, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 text-gray-800">{r.firstName}</td>
                            <td className="px-3 py-1.5 text-gray-800">{r.lastName}</td>
                            <td className="px-3 py-1.5 text-gray-500">{r.email || '—'}</td>
                            <td className="px-3 py-1.5">{r.contractType || 'CDI'}</td>
                            <td className="px-3 py-1.5">{r.weeklyHours || '35'}</td>
                            <td className="px-3 py-1.5">{r.conventionLevel || 'E1'}</td>
                          </tr>
                        ))}
                        {importRows.length > 10 && (
                          <tr><td colSpan={6} className="px-3 py-1.5 text-gray-400 italic">... et {importRows.length - 10} autres</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Result */}
              {importResult && (
                <div className={`rounded-lg p-4 ${importResult.errors.length === 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {importResult.errors.length === 0
                      ? <CheckCircle size={16} className="text-green-600" />
                      : <AlertCircle size={16} className="text-amber-600" />
                    }
                    <p className="text-sm font-semibold text-gray-800">
                      {importResult.created} employé{importResult.created > 1 ? 's' : ''} importé{importResult.created > 1 ? 's' : ''}
                      {importResult.errors.length > 0 && ` — ${importResult.errors.length} erreur${importResult.errors.length > 1 ? 's' : ''}`}
                    </p>
                  </div>
                  {importResult.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">Ligne {e.row} : {e.message}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                {importResult ? 'Fermer' : 'Annuler'}
              </button>
              {importRows.length > 0 && !importResult && (
                <button onClick={runImport} disabled={importing}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                  {importing ? 'Import en cours...' : `Importer ${importRows.length} employé${importRows.length > 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {modal === 'create' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <User size={18} className="text-blue-600" />
                <h2 className="font-bold text-gray-900">Nouvel employé</h2>
              </div>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Prénom *</label>
                  <input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className={input} placeholder="Thomas" />
                </div>
                <div>
                  <label className={label}>Nom *</label>
                  <input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className={input} placeholder="Martin" />
                </div>
              </div>
              <div>
                <label className={label}>Email</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={input} placeholder="email@exemple.fr" />
              </div>
              <div>
                <label className={label}>Téléphone</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={input} placeholder="06 00 00 00 00" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Contrat</label>
                  <select value={form.contractType} onChange={e => setForm(f => ({ ...f, contractType: e.target.value }))} className={input}>
                    {['CDI', 'CDD', 'Saisonnier', 'Extra', 'Stage', 'Intérim'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Heures/semaine</label>
                  <input type="number" value={form.weeklyHours} onChange={e => setForm(f => ({ ...f, weeklyHours: Number(e.target.value) }))} min={1} max={60} className={input} />
                </div>
              </div>

              {/* Niveau CCN IDCC 1505 */}
              <div className="border border-blue-100 bg-blue-50 rounded-lg p-3 space-y-3">
                <p className="text-xs font-semibold text-blue-700">Convention IDCC 1505</p>
                <div>
                  <label className={label}>Niveau CCN</label>
                  <select value={form.conventionLevel} onChange={e => handleLevelChange(e.target.value)} className={input}>
                    {CCN_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label} — {l.rate.toFixed(3)} €/h</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Taux horaire brut (€/h)</label>
                  <input type="number" step="0.001" value={form.hourlyRate}
                    onChange={e => setForm(f => ({ ...f, hourlyRate: Number(e.target.value) }))}
                    className={input} placeholder="12.063" />
                  <p className="text-xs text-blue-500 mt-1">Auto-rempli selon le niveau CCN. Modifiable si taux négocié supérieur.</p>
                </div>
              </div>

              <div>
                <label className={label}>Établissement *</label>
                <select value={form.establishmentId} onChange={e => setForm(f => ({ ...f, establishmentId: e.target.value }))} className={input}>
                  {establishments.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                Annuler
              </button>
              <button onClick={createEmployee} disabled={saving || !form.firstName || !form.lastName}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {saving ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
